import { useCreateMemoCategory, useRefreshMemoCategories, useUpdateMemoCategory } from '@app/sdk';
import { ComponentErrorBoundary } from '@app/utilities';
import { FormProvider, useForm } from 'react-hook-form';
import { FormContainer, Input, InputProps, TextAreaProps, TextArea } from '@app/shared';
import { MemoCategoryModel } from '@app/models';
import { toFormattedDate } from '@app/utilities';

export function MemoCategoryForm({ memoCategory }: { memoCategory: MemoCategoryModel }) {
  const { mutateAsync: create, isPending: isCreating } = useCreateMemoCategory();
  const { mutateAsync: update, isPending: isUpdating } = useUpdateMemoCategory();
  const refreshMemoCategories = useRefreshMemoCategories();

  async function onSaveReason(data: MemoCategoryModel) {
    let owningTeamsArray = data.owningTeamsArrayAsJoinedString
      ? data.owningTeamsArrayAsJoinedString.split('\n').filter((x) => x != '')
      : null;

    if (owningTeamsArray != null && owningTeamsArray.length == 0) {
      owningTeamsArray = null;
    }

    if (memoCategory?.id) {
      const request = {
        ...data,
        owningTeams: owningTeamsArray,
      };
      await update({ id: memoCategory.id, request });
    } else {
      const request = {
        ...data,
        owningTeams: owningTeamsArray,
      };
      await create(request);
    }
    refreshMemoCategories();
  }

  const formContext = useForm<MemoCategoryModel>();
  if (memoCategory?.owningTeams) {
    memoCategory.owningTeamsArrayAsJoinedString = memoCategory.owningTeams.join('\n');
  }

  const code: InputProps<MemoCategoryModel> = {
    name: 'code',
    label: 'Code',
    options: memoCategory?.id ? {} : { required: 'Code is required' },
    defaultValue: memoCategory?.code,
  };

  const description: InputProps<MemoCategoryModel> = {
    name: 'description',
    label: 'Description',
    options: { required: 'Description is required' },
    defaultValue: memoCategory?.description,
  };

  const expiryDate: InputProps<MemoCategoryModel> = {
    name: 'expiryDate',
    label: 'Expiry Date',
    defaultValue: toFormattedDate(memoCategory?.expiryDate),
    type: 'date',
  };

  const owningTeams: TextAreaProps<MemoCategoryModel> = {
    name: 'owningTeamsArrayAsJoinedString',
    label: 'Owning Teams',
    placeholder: 'Add teams separated by enter/new line',
    defaultValue: memoCategory?.owningTeamsArrayAsJoinedString,
  };

  return (
    <ComponentErrorBoundary>
      <FormProvider {...formContext}>
        <FormContainer isSubmitting={isCreating || isUpdating} onSubmit={onSaveReason} disableButtons>
          <Input {...code} disabled />
          <Input {...description} disabled />
          <Input {...expiryDate} disabled />
          <TextArea {...owningTeams} disabled />
        </FormContainer>
      </FormProvider>
    </ComponentErrorBoundary>
  );
}
